import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import OrderBand from '../components/OrderBand';
import { ArrowRight, Clock, Phone, Pin } from '../components/Icons';
import { LOCATIONS, getLocation, COPY, IZI } from '../data/site';
import { EVENTS } from '../data/events';
import { PHOTO } from '../data/media';
import './LocationDetail.css';

/** Distinct photography per branch so the two pages don't read as duplicates. */
const ART = {
  bugolobi: { hero: PHOTO.plate8, a: PHOTO.plate1, b: PHOTO.burger },
  kyanja: { hero: PHOTO.plate1, a: PHOTO.plate4, b: PHOTO.signatures },
};

export default function LocationDetail() {
  const { slug } = useParams();
  const loc = getLocation(slug);

  if (!loc) return <Navigate to="/#locations" replace />;

  const other = LOCATIONS.find((l) => l.id !== loc.id);
  const art = ART[loc.id] || ART.bugolobi;
  const todayIndex = new Date().getDay();

  return (
    <>
      <Seo
        title={`${loc.name} — ${loc.area}`}
        description={`${loc.welcome} Open daily. Call ${loc.phone} to book.`}
        path={`/locations/${loc.id}`}
      />

      <PageHero eyebrow="Our locations" title={loc.name} lead={loc.welcome} image={art.hero}>
        <Link to={`/bookings?branch=${loc.id}`} className="btn btn--light">
          <span>Book this branch</span>
          <ArrowRight />
        </Link>
        <a className="btn btn--outline-light" href={loc.mapUrl} target="_blank" rel="noopener noreferrer">
          <Pin size={15} />
          <span>Get directions</span>
        </a>
      </PageHero>

      {/* Facts ------------------------------------------------------------- */}
      <section className="section--tight locfacts">
        <div className="page locfacts__inner">
          <a className="locfacts__item" href={`tel:${loc.phoneIntl}`}>
            <Phone size={16} />
            <span>
              <small>Call the branch</small>
              <strong className="tabular">{loc.phone}</strong>
            </span>
          </a>
          <a className="locfacts__item" href={loc.mapUrl} target="_blank" rel="noopener noreferrer">
            <Pin size={16} />
            <span>
              <small>Find us</small>
              <strong>{loc.area}</strong>
            </span>
          </a>
          <div className="locfacts__item">
            <Clock size={16} />
            <span>
              <small>Open</small>
              <strong>Every day, from 9am</strong>
            </span>
          </div>
        </div>
      </section>

      {/* Welcome ----------------------------------------------------------- */}
      <section className="section">
        <div className="page split split--wide-right">
          <Reveal className="locintro__media" variant="mask">
            <div className="frame frame--3x4 frame--hover locintro__a">
              <img src={art.a} alt={`Food at The Local ${loc.name}`} loading="lazy" />
            </div>
            <div className="frame frame--1x1 frame--hover locintro__b">
              <img src={art.b} alt="" loading="lazy" />
            </div>
          </Reveal>

          <div className="stack stack-md">
            <SectionHeading eyebrow={loc.strapline} title={COPY.introHeading} />
            <Reveal as="p" className="body" delay={0.08}>
              {COPY.introBody}
            </Reveal>
            <Reveal as="p" className="body" delay={0.14}>
              {loc.note}
            </Reveal>
            <Reveal className="locintro__actions" delay={0.2}>
              <Link to="/menus" className="btn btn--ghost">
                <span>View menus</span>
                <ArrowRight />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Hours ------------------------------------------------------------- */}
      <section className="section section--deep">
        <div className="page split">
          <div className="stack stack-md">
            <SectionHeading eyebrow="Opening hours" title="When we're here." size="d3" />
            <ul className="lochours">
              {loc.hours.map((h) => (
                <Reveal as="li" key={h.days} className="lochours__row">
                  <span>{h.days}</span>
                  <span className="tabular">{h.time}</span>
                </Reveal>
              ))}
            </ul>
            <Reveal as="p" className="lochours__note" delay={0.15}>
              Kitchen times can shift on public holidays. Call{' '}
              <a href={`tel:${loc.phoneIntl}`} className="tabular">
                {loc.phone}
              </a>{' '}
              if you&apos;re planning around one.
            </Reveal>
          </div>

          <div className="stack stack-md">
            <SectionHeading eyebrow="What's on here" title="Every week has a reason." size="d3" />
            <ul className="locevents">
              {EVENTS.map((e, i) => (
                <Reveal as="li" key={e.id} delay={i * 0.06}>
                  <Link
                    to={`/whats-on/${e.id}`}
                    className={`locevent${e.day.includes(todayIndex) ? ' is-today' : ''}`}
                  >
                    <span className="locevent__name">{e.name}</span>
                    <span className="locevent__when tabular">
                      {e.when} · {e.time}
                    </span>
                    <ArrowRight />
                  </Link>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <OrderBand compact />

      {/* Other branch ------------------------------------------------------ */}
      {other ? (
        <section className="section otherbranch">
          <div className="page otherbranch__inner">
            <div>
              <p className="eyebrow">The other room</p>
              <h2 className="display d2">{other.name}</h2>
              <p className="lead">{other.note}</p>
            </div>
            <div className="otherbranch__actions">
              <Link to={`/locations/${other.id}`} className="btn">
                <span>Go to {other.name}</span>
                <ArrowRight />
              </Link>
              <a className="link" href={`tel:${other.phoneIntl}`}>
                Call {other.phone} <ArrowRight />
              </a>
            </div>
          </div>
        </section>
      ) : null}

      <section className="section--tight section--deep">
        <div className="page evbranches">
          <p className="eyebrow">Delivery from this branch</p>
          <div className="evbranches__links">
            <a className="link" href={IZI.web} target="_blank" rel="noopener noreferrer">
              Order on {IZI.brand} <ArrowRight />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
